import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { testsAPI } from '../services/api';
import {
  Box,
  Typography,
  Container,
  Grid,
  Alert,
  Tab,
  Pagination,
  Stack,
} from '@mui/material';
import StatsCard from '../components/StatsCard';
import TestResultCard from '../components/TestResultCard';
import Tabs from '../components/UIKit/Tabs';
import { toast } from 'react-toastify';

const TestResults = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['tests', 'common']);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 6,
  });

  
  const fetchTestResults = useCallback(async (page = pagination.currentPage) => {
    try {
      // Создаем параметры запроса в зависимости от выбранной вкладки
      const params = {
        page,
        limit: pagination.perPage,
      };
      if (tabValue === 1) {
        params.passed = true;
      } else if (tabValue === 2) {
        params.passed = false;
      } else if (tabValue === 3) {
        params.hasPendingReview = true;
      }
      const response = await testsAPI.getUserResults(params);
      setResults(response.data.results);
      setPagination({
        currentPage: response.data.pagination.currentPage,
        totalPages: response.data.pagination.totalPages,
        totalItems: response.data.pagination.totalItems,
        perPage: response.data.pagination.perPage,
      });
    } catch (error) {
      console.error('Ошибка при загрузке результатов тестов:', error);
      setError(t('tests:results.error_loading_tests'));
    }
  }, [pagination.currentPage, pagination.perPage, t, tabValue]);

  useEffect(() => {
    // При смене вкладки сбрасываем пагинацию на первую страницу
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchTestResults(1);
    fetchTestStatistics();
  }, [fetchTestResults, tabValue]);

  const fetchTestStatistics = async () => {
    try {
      const response = await testsAPI.getUserResultsStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке статистики тестов:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePageChange = (event, page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
    fetchTestResults(page);
  };

  const handleRetakeTest = async (testId) => {
    try {
      const testInfo = await testsAPI.getTestInfo(testId);
      const { courseSlug, moduleSlug, lessonSlug } = testInfo.data;
      const path = lessonSlug
        ? `/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${testId}`
        : `/courses/${courseSlug}/modules/${moduleSlug}/tests/${testId}`;
      navigate(path);
    } catch (error) {
      toast.error(t('tests:errors.startTestFailed'));
    }
  };

  const handleViewDetails = (resultId) => {
    navigate(`/test-results/${resultId}`);
  };

  // Рендеринг списка тестов
  const renderTestResults = () => {
    // Используем данные, уже отфильтрованные сервером
    const filteredResults = results;

    if (filteredResults.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          {tabValue === 0
            ? t('tests:results.no_test_results')
            : tabValue === 1
            ? t('tests:results.no_completed_tests')
            : tabValue === 2
            ? t('tests:results.no_incomplete_tests')
            : t('tests:results.no_pending_tests')}
        </Alert>
      );
    }

    return (
      <>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {filteredResults.map((result) => (
            <Grid item xs={12} md={6} key={result._id}>
              <TestResultCard
                result={result}
                onRetake={handleRetakeTest}
                onViewDetails={handleViewDetails}
              />
            </Grid>
          ))}
        </Grid>

        {pagination.totalPages > 1 && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={handlePageChange}
              color="primary"
              siblingCount={1}
              boundaryCount={1}
              showFirstButton
              showLastButton
              size="medium"
              sx={{
                '& .MuiPaginationItem-root': {
                  mx: { xs: 0.5, sm: 1 },
                },
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {t('common:pagination.showing_results_simple')
                .replace(
                  '$1',
                  (pagination.currentPage - 1) * pagination.perPage + 1
                )
                .replace(
                  '$2',
                  Math.min(
                    pagination.currentPage * pagination.perPage,
                    pagination.totalItems
                  )
                )
                .replace('$3', pagination.totalItems)}
            </Typography>
          </Stack>
        )}
      </>
    );
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container sx={{ py: { xs: 3, sm: 4 } }}>
      <Typography
        variant="h3"
        component="h1"
        gutterBottom
        sx={{
          mb: { xs: 2, sm: 3 },
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
        }}
      >
        {t('tests:results.title')}
      </Typography>

      <StatsCard stats={stats} />

      <Box
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          p: '4px',
          mb: 3,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="test result tabs"
          swapBackground
        >
          <Tab label={t('tests:results.all_tests')} />
          <Tab label={t('tests:results.completed_tests')} />
          <Tab label={t('tests:results.not_completed_tests')} />
          <Tab label={t('tests:results.pending_review')} />
        </Tabs>
      </Box>

      {pagination.totalItems > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('common:pagination.showing_results_simple')
              .replace(
                '$1',
                (pagination.currentPage - 1) * pagination.perPage + 1
              )
              .replace(
                '$2',
                Math.min(
                  pagination.currentPage * pagination.perPage,
                  pagination.totalItems
                )
              )
              .replace('$3', pagination.totalItems)}
          </Typography>
        </Box>
      )}

      {renderTestResults()}
    </Container>
  );
};

export default TestResults;
